// ================== Konfigurasi stopwords ==================

/** Gelar akademik yang sering nempel di nama dosen, diabaikan saat matching */
const DOSEN_TITLE_STOPWORDS = new Set([
    // Gelar Depan
    "prof", "dr", "ir", "drs", "pdt",

    // Gelar Belakang - Sarjana & Diploma (S1 / D3)
    "s.kom", "s.t", "s.si", "s.pd", "s.e", "s.a.p", "s.sos",
    "s.ip", "s.h.i", "s.h", "s.th", "s.mat", "a.md.kom",

    // Gelar Belakang - Magister (S2)
    "m.kom", "m.t", "m.si", "m.pd", "m.e", "m.a.p", "m.cs",
    "m.sc", "m.a", "m.m", "m.pd.k", "m.ag", "m.aktr", "m.h.i",
    "m.e.i.l", "m.m.s.i", "m.sos",

    // Gelar Belakang - Doktor / Sertifikasi Profesi (S3 & Profesi)
    "ph.d", "d.sc", "ipm", "asean eng"
]);

/** Kata umum bahasa Indonesia yang diabaikan saat matching */
const GENERIC_STOPWORDS = new Set([
    "dan", "di", "ke", "dari", "yang", "untuk", "pada", "dengan",
    "atau", "saya", "aku", "gue", "pak", "bu", "ibu", "bapak", "dosen",
    "matkul", "mata", "kuliah", "prodi", "jurusan",
]);

// ================== Helper string ==================

function normalize(text: string): string {
    return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // hilangkan diakritik
        .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, " ") // hilangkan tanda baca
        .replace(/\s+/g, " ")
        .trim();
}

function tokenize(text: string): string[] {
    return normalize(text).split(" ").filter(Boolean);
}

/** Levenshtein distance sederhana (jumlah edit minimal antar 2 string) */
function levenshtein(a: string, b: string): number {
    const m = a.length;
    const n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;

    const dp: number[][] = Array.from({ length: m + 1 }, () =>
        new Array(n + 1).fill(0)
    );

    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;

    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            dp[i][j] = Math.min(
                dp[i - 1][j] + 1, // deletion
                dp[i][j - 1] + 1, // insertion
                dp[i - 1][j - 1] + cost // substitution
            );
        }
    }

    return dp[m][n];
}

/** Similarity ratio 0..1 antara 2 string, berbasis levenshtein distance */
function similarity(a: string, b: string): number {
    const maxLen = Math.max(a.length, b.length);
    if (maxLen === 0) return 1;
    return 1 - levenshtein(a, b) / maxLen;
}

// ================== Fuzzy match generic ==================

interface MatchOptions {
    /** Skor minimum (0..1) supaya kandidat dianggap match. Default 0.55 */
    threshold?: number;
    /** Token dengan panjang di bawah ini dianggap noise (misal sisa "s","t" dari gelar) */
    minTokenLength?: number;
    /** Stopword tambahan khusus untuk kasus tertentu */
    extraStopwords?: string[];
}

/**
 * Fuzzy match generic: cari kandidat (dari list nama panjang, misal nama
 * matkul/dosen/prodi) yang paling relevan dengan teks bebas dari user.
 *
 * Strategi (dari yang paling pasti ke yang paling toleran):
 * 1. Substring langsung (case/diakritik-insensitive) → match instan.
 * 2. Token overlap: hitung berapa token signifikan dari kandidat yang
 *    muncul (persis atau fuzzy, typo-tolerant) di teks user, dibobot
 *    berdasarkan panjang token (token panjang = lebih signifikan).
 * 3. Fallback: similarity keseluruhan string (berguna untuk nama pendek
 *    seperti "Agama" yang tidak punya banyak token).
 *
 * Return null kalau skor terbaik di bawah threshold (tidak ada match yang
 * cukup yakin), supaya caller bisa fallback ke template "tidak ditemukan".
 */
export function fuzzyMatch(
    userText: string,
    candidates: string[],
    options: MatchOptions = {}
): string | null {
    const { threshold = 0.55, minTokenLength = 3, extraStopwords = [] } = options;

    const stopwords = new Set([
        ...GENERIC_STOPWORDS,
        ...DOSEN_TITLE_STOPWORDS,
        ...extraStopwords,
    ]);

    const normalizedUserText = normalize(userText);
    const userTokens = tokenize(userText);

    let bestCandidate: string | null = null;
    let bestScore = 0;

    for (const candidate of candidates) {
        const normalizedCandidate = normalize(candidate);
        if (!normalizedCandidate) continue;

        // 1. Substring langsung → hampir pasti ini yang dimaksud
        if (normalizedUserText.includes(normalizedCandidate)) {
            return candidate;
        }

        // Token signifikan dari kandidat (buang gelar, kata umum, token pendek)
        const candidateTokens = tokenize(candidate).filter(
            (t) => t.length >= minTokenLength && !stopwords.has(t)
        );

        let tokenScore = 0;
        if (candidateTokens.length > 0) {
            let matchedWeight = 0;
            let totalWeight = 0;

            for (const cToken of candidateTokens) {
                const weight = cToken.length;
                totalWeight += weight;

                // cari token user paling mirip dengan token kandidat ini
                let bestTokenScore = 0;
                for (const uToken of userTokens) {
                    if (uToken.length < 2 || stopwords.has(uToken)) continue;
                    const score = similarity(cToken, uToken);
                    if (score > bestTokenScore) bestTokenScore = score;
                }

                // toleran typo: token dianggap "match" kalau cukup mirip (>=0.75)
                if (bestTokenScore >= 0.75) {
                    matchedWeight += weight * bestTokenScore;
                }
            }

            tokenScore = totalWeight > 0 ? matchedWeight / totalWeight : 0;
        }

        // 2. Fallback skor keseluruhan string (untuk nama pendek/tanpa token unik)
        const wholeScore = similarity(normalizedUserText, normalizedCandidate);

        const finalScore = Math.max(tokenScore, wholeScore);

        if (finalScore > bestScore) {
            bestScore = finalScore;
            bestCandidate = candidate;
        }
    }

    return bestScore >= threshold ? bestCandidate : null;
}